// Pickup orders headed to the recycler, straight from the shared core story
// (generatePickupOrders) so their ids, weights and dates match the other apps.
// Shaped here as the market's IncomingPickupOrder / PreOrderLinkedPickup.

import type { IncomingPickupOrder, IncomingPickupPagination, PreOrderLinkedPickup } from '../../types/pre-order.type';
import {
  DEMO_OUTLET_NAME,
  facilityById,
  generatePickupOrders,
  materialById,
  paginate,
  siteById,
  type DemoPickupOrder,
} from '../core';

let pickups: DemoPickupOrder[] = [];

export function resetPickups(): void {
  pickups = generatePickupOrders();
}

export function allPickups(): readonly DemoPickupOrder[] {
  return pickups;
}

export function pickupById(id: string): DemoPickupOrder | undefined {
  return pickups.find((p) => p.id === id);
}

/** A completed pickup the recycler did not reject: the only kind that counts as received supply. */
export function countsAsReceived(p: DemoPickupOrder): boolean {
  return p.status === 'completed' && p.approval_status !== 'rejected';
}

/** Weight the pickup contributes to a pre-order (verified weight, as the backend counts it). */
export function pickupFulfilledKg(p: DemoPickupOrder): number {
  if (!countsAsReceived(p)) return 0;
  return p.verified_kg ?? p.estimated_kg;
}

/** The authoritative amount on the order: the verified weight once weighed. */
function pickupAmountKg(p: DemoPickupOrder): number {
  return p.verified_kg ?? p.estimated_kg;
}

function outletLabel(p: DemoPickupOrder): string {
  const site = siteById(p.site_id);
  return site ? `${DEMO_OUTLET_NAME} – ${site.name_en}` : DEMO_OUTLET_NAME;
}

function deliveryPhase(p: DemoPickupOrder): string | null {
  if (p.status === 'completed') return 'completed';
  if (p.status === 'in_transit') return 'on_way_to_recycler';
  return null;
}

export function toIncomingPickup(p: DemoPickupOrder): IncomingPickupOrder {
  const material = materialById(p.material_id);
  const site = siteById(p.site_id);
  const facility = p.facility_id ? facilityById(p.facility_id) : undefined;
  return {
    id: p.id,
    amount: pickupAmountKg(p),
    amount_unit: 'kg',
    status: p.status,
    organization_id: p.outlet_org_id,
    material_id: p.material_id,
    material_name_snapshot: material?.name_en ?? null,
    material_type: {
      id: material?.material_type_id ?? p.material_id,
      name: material?.name_en ?? p.material_id,
      parent_category: material?.category_code ?? 'other',
      material_id: p.material_id,
    },
    data: {
      reference: p.reference,
      recycler_id: p.recycler_org_id,
      collection_site_id: p.site_id,
      collection_site_name: site?.name_en ?? null,
      collection_site_name_ar: site?.name_ar ?? null,
      waste_outlet_name: DEMO_OUTLET_NAME,
      container_id: p.container_id,
      fill_level: p.fill_level,
      estimated_kg: p.estimated_kg,
      verified_weight_kg: p.verified_kg,
      approval_status: p.approval_status,
      sale_amount_sar: p.sale_amount_sar,
      facility_id: p.facility_id,
      facility_name: facility?.name_en ?? null,
      truck_id: p.truck_id,
      driver_id: p.driver_id,
      scheduled_at: p.scheduled_at,
      completed_at: p.completed_at,
    },
    created_at: p.created_at,
  };
}

export function toLinkedPickup(p: DemoPickupOrder): PreOrderLinkedPickup {
  return {
    pickup_order_id: p.id,
    status: p.status,
    amount: pickupAmountKg(p),
    amount_unit: 'kg',
    verified_weight_kg: p.verified_kg,
    waste_outlet_id: p.outlet_org_id,
    waste_outlet_name: outletLabel(p),
    delivery_phase: deliveryPhase(p),
    created_at: p.created_at,
  };
}

export interface IncomingQuery {
  recyclerOrgId: string;
  unlinkedOnly: boolean;
  materialCategory?: string;
  linkedIds: ReadonlySet<string>;
  page?: unknown;
  limit?: unknown;
}

/**
 * GET /pickup_order/incoming: completed (not rejected) pickups delivered to the
 * recycler, newest first, optionally only those not yet attached to a pre-order.
 */
export function listIncoming(q: IncomingQuery): IncomingPickupPagination {
  const rows = pickups
    .filter((p) => p.recycler_org_id === q.recyclerOrgId && countsAsReceived(p))
    .filter((p) => !q.unlinkedOnly || !q.linkedIds.has(p.id))
    .filter((p) => !q.materialCategory || materialById(p.material_id)?.category_code === q.materialCategory)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const page = paginate(rows, q.page, q.limit);
  return {
    pickup_orders: page.items.map(toIncomingPickup),
    total: page.total,
    page: page.page,
    limit: page.limit,
  };
}
